import { Model, Store } from "@bryntum/schedulerpro";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { HasProjectMixin } from "../models/mixins/HasProjectMixin.jsx";
import { getCompanyCamDocuments, getCompanyCamPhotos, getCompanyCamProject } from "../../network/network.jsx";
import { SwivuDocumentType } from "../../other/constants/OtherKeys.jsx";
import { getAging, getFormattedFileSize } from "../../other/utils/utils.jsx";

class SwivuFile extends HasProjectMixin(Model) {
  static get $name() {
    return "SwivuFile"
  }

  static get fields() {
    return [
      { name: "originalRecordId", type: "number", persist: false },
      { name: "boardId", dataSource: "board", type: "integer" },
      { name: "source", type: "number", persist: false },
      { name: "name", type: "string", persist: false },
      { name: "addedBy", type: "string", persist: false },
      { name: "canEdit", dataSource: "can_edit_grant", type: "integer", persist: false },
      { name: "canView", dataSource: "can_view_grant", type: "integer", persist: false },
      { name: "age", type: "integer", persist: false },
      { name: "fileType", dataSource: "file_type", type: "string", persist:  false },

      { name: "description", dataSource: "description", type: "string", persist: false },
      { name: "size", dataSource: "byte_size", type: "string", persist: false },
      { name: "lastUpdatedAt", persist: false },
      { name: "lastUpdatedBy", persist: false },

      { name: "taskId", dataSource: "task", type: "integer" },
      { name: "resourceId", dataSource: "resource", type: "integer" },
      { name: "clientId", dataSource: "client", type: "integer" },
      { name: "url", dataSource: "url", persist: false },

      // CC image specific fields
      { name: "thumbnailImageUrl", persist: false },
      { name: "originalImageUrl", persist: false },
    ]
  }

  get documentDetails() {
    const documentRecord = this;
    if (documentRecord) {
      return [documentRecord];
    }
    return [];
  }

  get entity() {
    if (!this.project) return null;
    if (this.resourceId) return this.project.resourceStore.getById(this.resourceId);
    if (this.taskId) return this.project.extraTaskStore.getById(this.taskId);
    if (this.clientId) return this.project.clientsStore.getById(this.clientId);
    return null;
  }
}

class SwivuFilesStore extends Store {

  construct(args) {
    super.construct();

    this.swivuProject = args.swivuProject;
    this.swivuEntityRecord = args.swivuEntityRecord;
    this.swivuEntityRecordType = args.swivuEntityRecordType;
    this.swivuActiveEntitiesFn = args.swivuActiveEntitiesFn;
  }

  static get configurable() {
    return {
      // lazyLoad: { chunkSize : 1000 }, // for now, I don't want to utilize this feature as it causes issues with the filters check above
      // autoLoad : false,
      modelClass: SwivuFile
    }
  }

  static swivuGetMappedCCDocumentRecord(doc, isCCImage = false) {
    const CCData = {
      id: Model.generateId("swiuv-cc-document"),
      originalRecordId: doc.id,
      boardId: null,
      source: SwivuDocumentType.ccDocument,
      name: doc.name,
      addedBy: doc.creator_name,
      canEdit: null,
      canView: null,
      size: getFormattedFileSize(doc.byte_size),
      aging: getAging(new Date(doc.created_at * 1000).toISOString()),
      description: doc.description,
      lastUpdatedAt: new Date(doc.updated_at * 1000).toISOString(),
      lastUpdatedBy: doc.updaterName,
      url: doc.url,
    }

    if (isCCImage) {
      let thumbImageUrl = doc.uris.find((uri) => uri.type === "thumbnail_annotation");
      if (!thumbImageUrl) thumbImageUrl = doc.uris.find((uri) => uri.type === "thumbnail");

      let originalImageUrl = doc.uris.find((uri) => uri.type === "original_annotation");
      if (!originalImageUrl) originalImageUrl = doc.uris.find((uri) => uri.type === "original");

      CCData.name = doc.id;
      CCData.thumbnailImageUrl = thumbImageUrl.url;
      CCData.originalImageUrl = originalImageUrl.url;
    }

    return CCData;
  }

  // since each new editor/tab instance register this listener and from any editor the record gets added this listener also gets called
  // so if there are two editors opened and one of them adds a new note, this listener will be called with other editor added data, so adding check of swivuEntityRecordType
  swivuIsThisEditorRecord(record) {
    return record && record[this.swivuEntityRecordType] === this.swivuEntityRecord.id;
  }

  // Todo: Listeners needs to be destroyed
  swivuAttachUpdateListenersOnOriginalStores() {
    if (!this.destroyListenerFilesStore) {
      this.destroyListenerFilesStore = this.swivuProject.getCrudStore(STORE_KEYS.boardFilesStore).on({
        idChange(e) {
          if (this.swivuIsThisEditorRecord(e.record)) {
            this.insert(0, { ...e.record.swivuDocumentRecordMapping, id: Model.generateId("swiuv-board-file") });
          }
        },
        add(e) {
          // This is to handle the case when a new file is added and received via the websocket
          const newRecord = e.records[0];
          if (!newRecord.isPhantom && !this.getById(newRecord.id)) {
            this.insert(0, newRecord.swivuDocumentRecordMapping);
          }
        },
        update(e) {
          if (this.swivuIsThisEditorRecord(e.record)) {
            const existingRecord = this.find((r) => r.originalRecordId === e.record.id && r.source === SwivuDocumentType.swivuFile);
            if (existingRecord) {
              existingRecord.set(e.record.swivuDocumentRecordMapping);
            }
          }
        },
        remove(e) {
          if (this.swivuIsThisEditorRecord(e.records[0]) && e.records.length) {
            const existingRecord = this.find((r) => r.originalRecordId === e.records[0].id && r.source === SwivuDocumentType.swivuFile);
            if (existingRecord) {
              this.remove(existingRecord);
            }
          }
        },
        thisObj: this,
      });
    }

    if (!this.destroyListenerGridStore) {
      // Update changes in the original store whenever any data update in the grid
      this.destroyListenerGridStore = this.on("update", ({ record, changes }) => {
        const updatedChanges = Object.keys(changes).reduce((prev, propertyName) => {
          prev[propertyName] = changes[propertyName].value;
          return prev;
        }, {});

        const originalStore = this.swivuProject.getCrudStore(STORE_KEYS.boardFilesStore);
        const originalRecord = originalStore.getById(record.originalRecordId);

        if (originalRecord) {
          if ("canEdit" in updatedChanges) {
            originalRecord.canEdit = updatedChanges.canEdit;
          }

          if ("canView" in updatedChanges) {
            originalRecord.canView = updatedChanges.canView;
          }

          if ("description" in updatedChanges) {
            originalRecord.description = updatedChanges.description;
          }
        }
      })
    }
  }

  swivuDetachUpdateListenersFromOriginalStores() {
    if (this.destroyListenerFilesStore) {
      this.destroyListenerFilesStore();
      this.destroyListenerFilesStore = null;
    }

    if (this.destroyListenerGridStore) {
      this.destroyListenerGridStore();
      this.destroyListenerGridStore = null;
    }
  }

  async swivuGetData(isReload, maxFiles) {

    const enabledEntities = this.swivuActiveEntitiesFn();
    let boardFilesRecords = [];

    if (enabledEntities.isActiveCCEntity && (isReload || !this.swivuCompanyCamRecords)) {
      if (this.swivuEntityRecord.companyCamProject) {
        const companyCamProjectRecord = this.swivuProject.extraBoardIntegrationsStore.find((item) => item.key === "companyCam");
        if (companyCamProjectRecord) {
          try {
            this.swivuCompanyCamRecords = [];
            const project = await getCompanyCamProject(this.swivuEntityRecord.companyCamProject, companyCamProjectRecord.value);
            const maxFileCount = (maxFiles && project.data.photo_count > maxFiles) ? maxFiles : project.data.photo_count;
            const totalCallsToMake = Math.ceil(maxFileCount / 100);

            // cc doesn't allow fetching all photos in one call, so we need to make multiple calls
            const photoPromises = Array.from({ length: totalCallsToMake }, (_, i) =>
              getCompanyCamPhotos(this.swivuEntityRecord.companyCamProject, companyCamProjectRecord.value, {
                per_page: 100,
                page: i + 1
              })
            );

            const photoResults = await Promise.all(photoPromises);
            photoResults.forEach(result => {
              this.swivuCompanyCamRecords.push(...result.data.map(doc =>
                SwivuFilesStore.swivuGetMappedCCDocumentRecord(doc, true)
              ));
            });

            await getCompanyCamDocuments(this.swivuEntityRecord.companyCamProject, companyCamProjectRecord.value, { per_page: undefined }).then((docs) => {
              this.swivuCompanyCamRecords.push(...docs.data.map((doc) => SwivuFilesStore.swivuGetMappedCCDocumentRecord(doc)));
            });
          } catch (e) {
            // Todo: this should be communicated in the UI
            console.log("Error fetching CompanyCam documents:", e);
          }
        }
      } else {
        this.swivuCompanyCamRecords = [];
      }
    }

    boardFilesRecords = this.swivuProject.getCrudStore(STORE_KEYS.boardFilesStore)
      .query((record) => record[this.swivuEntityRecordType] === this.swivuEntityRecord.id, true)
      .map((record) => record.swivuDocumentRecordMapping);

    const allRecords = [...boardFilesRecords, ...(this.swivuCompanyCamRecords || [])];

    return {
      data: allRecords,
      total: allRecords.length,
    }
  }

  /*async requestData({startIndex, count}){
    console.log({ startIndex, count });
    const { data, total } = await this.swivuGetData({ startIndex, count });

    return {
      data,
      total
    }
  }*/
}

export default SwivuFilesStore;
